import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { OVERRIDABLE_THEME_VARS } from '@/theme/theme.config';
import type { OverridableThemeVar } from '@/theme/theme.config';

export type ThemeMode = 'light' | 'dark' | 'system';

/** CSS variable overrides applied by a tenant to customise the platform palette. */
export interface TenantTheme {
  vars?: Partial<Record<OverridableThemeVar, string>>;
  brandName?: string;
  logoUrl?: string;
}

interface ThemeContextValue {
  mode: ThemeMode;
  resolvedMode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  tenantTheme: TenantTheme | null;
  applyTenantTheme: (theme: TenantTheme | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'service-portal:theme-mode';

function getSystemMode(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  return mode === 'system' ? getSystemMode() : mode;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'system';
  });

  const [tenantTheme, setTenantTheme] = useState<TenantTheme | null>(null);

  const resolvedMode = useMemo(() => resolveMode(mode), [mode]);

  // Apply dark/light class to <html>
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedMode);
  }, [resolvedMode]);

  // Listen for OS theme changes when mode === 'system'
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(getSystemMode());
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  // Apply / clear tenant CSS variable overrides on <html>
  useEffect(() => {
    const root = document.documentElement;
    if (tenantTheme?.vars !== undefined) {
      for (const key of OVERRIDABLE_THEME_VARS) {
        const value = tenantTheme.vars[key];
        if (value !== undefined) {
          root.style.setProperty(key, value);
        } else {
          root.style.removeProperty(key);
        }
      }
    } else {
      for (const key of OVERRIDABLE_THEME_VARS) {
        root.style.removeProperty(key);
      }
    }
  }, [tenantTheme]);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolvedMode, setMode, tenantTheme, applyTenantTheme: setTenantTheme }),
    [mode, resolvedMode, setMode, tenantTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) throw new Error('useThemeContext must be used within ThemeProvider');
  return ctx;
}
